import { MailX } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext, useParams, useSearchParams } from 'react-router';
import { EntitySheet } from '@/components/entity-sheet/EntitySheet';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { sectionPath } from '../place/place';
import { StateMessage } from '../ui/StateMessage';
import { InvitationCard } from './InvitationCard';
import type { InvitationsContext } from './InvitationsPage';
import type { Invitation } from './use-invitations';

/** What the card leaves in the history entry it closes to: whose row takes the focus back. */
export type InvitationClosing = { readonly focusInvitation: string };

/**
 * The invitation's card (decision 101, #401), the first card built on the shared parts in
 * `components/entity-sheet`. It is read from the list it was opened from: the administration API
 * has no read of one invitation, and the list already holds every field an invitation has. So a
 * card whose invitation the list does not hold (filtered out, withdrawn elsewhere, an old link)
 * says it is not here, and leads back to the list.
 *
 * Two parts of the board wait for the API: «Chi l'ha mandato» as a link with «Altri suoi inviti»
 * (the invitation names its inviter, without an id), and the delivery events in the story.
 */
export function InvitationSheet() {
  const { t } = useTranslation('administration');
  const { place, detail, items, loading, actions } = useOutletContext<InvitationsContext>();
  const { invitationId = '' } = useParams();
  const [params] = useSearchParams();
  const titleId = useId();
  const query = params.size > 0 ? `?${params.toString()}` : '';
  const listPath = sectionPath(place, 'invitations');
  const back = `${listPath}${query}`;
  const closing: InvitationClosing = { focusInvitation: invitationId };
  const index = items.findIndex((item) => item.id === invitationId);
  const invitation = index >= 0 ? items[index] : undefined;
  const neighbour = (item: Invitation | undefined, key: 'previous' | 'next') => {
    if (item === undefined) return null;
    return {
      to: `${listPath}/${item.id}${query}`,
      label: t(`invitations.card.${key}`, { email: item.email }),
    };
  };

  return (
    <EntitySheet
      titleId={titleId}
      back={back}
      closing={closing}
      backLabel={t('invitations.card.back')}
      closeLabel={t('invitations.card.close')}
      previous={neighbour(items[index - 1], 'previous')}
      next={index >= 0 ? neighbour(items[index + 1], 'next') : null}
    >
      {invitation !== undefined ? (
        <InvitationCard
          invitation={invitation}
          titleId={titleId}
          placeName={detail.place.name}
          rolesPath={sectionPath(place, 'roles')}
          peoplePath={sectionPath(place, 'people')}
          actions={actions}
        />
      ) : (
        <GoneOrLoading loading={loading} titleId={titleId} back={back} closing={closing} />
      )}
    </EntitySheet>
  );
}

function GoneOrLoading({
  loading,
  titleId,
  back,
  closing,
}: {
  readonly loading: boolean;
  readonly titleId: string;
  readonly back: string;
  readonly closing: InvitationClosing;
}) {
  const { t } = useTranslation('administration');
  if (loading) {
    return (
      <div>
        <h2 id={titleId} className="sr-only">
          {t('common.loading')}
        </h2>
        <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-3">
          <Skeleton className="h-12 w-60" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-32 w-full" />
        </SkeletonRegion>
      </div>
    );
  }
  return (
    <div>
      <h2 id={titleId} className="sr-only">
        {t('invitations.card.gone.title')}
      </h2>
      <StateMessage
        role="alert"
        icon={MailX}
        title={t('invitations.card.gone.title')}
        text={t('invitations.card.gone.text')}
        action={
          <Button asChild variant="outline">
            <Link to={back} state={closing}>
              {t('invitations.card.back')}
            </Link>
          </Button>
        }
      />
    </div>
  );
}
